import {defineStore} from "pinia";
import {SubscriptionPlan} from "@/subscription-plan/interfaces";
import {subscriptionPlanApi} from "@/container";

interface SubscriptionPlanStore {
    subscriptionPlans?: SubscriptionPlan[];
    loading: boolean;
    /** When the plans were last asked for, the answer may still be on its way. Reset on failure. */
    requestedAt: number | null;
}

/**
 * Subscription plan store.
 */
export const useSubscriptionPlanStore = defineStore(
    'subscriptionPlan',
    {
        state: (): SubscriptionPlanStore => ({
            subscriptionPlans: undefined,
            loading: false,
            requestedAt: null,
        }),
        actions: {
            /**
             * Load the subscription plans from the API.
             */
            async loadSubscriptionPlans() {
                this.loading = true;
                try {
                    await this.refreshSubscriptionPlans();
                } finally {
                    this.loading = false;
                }
            },
            /**
             * Refresh the subscription plans from the API.
             */
            async refreshSubscriptionPlans() {
                this.requestedAt = Date.now();
                try {
                    this.subscriptionPlans = await subscriptionPlanApi().index();
                } catch (error) {
                    this.requestedAt = null;
                    throw error;
                }
            },
        },
        getters: {
            /**
             * Get the global subscription plan.
             */
            globalPlan(): SubscriptionPlan | undefined {
                return this.subscriptionPlans?.find(plan => plan.is_global);
            },
            /**
             * Get the individual subscription plans.
             */
            individualPlans(): SubscriptionPlan[] {
                return this.subscriptionPlans?.filter(plan => !plan.is_global) || [];
            },
            /** Returns the subscription plan by slug. */
            subscriptionPlanBySlug(state): (slug: string) => SubscriptionPlan | undefined {
                return (slug: string): SubscriptionPlan | undefined => {
                    return state.subscriptionPlans?.find(subPlan => subPlan.slug === slug);
                };
            },
        }
    }
);
